class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedCounts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Only close if the input is empty - keep suggestions visible when there's text
        if (!this.getQuery().length) {
          this.close();
        }
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

updateSearchForTerm(previousTerm, newTerm) {
    // Find the element containing the "Search for '...'" text
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    // Use optional chaining just in case the element isn't found
    const currentButtonText = searchForTextElement?.innerText;

    // Proceed only if we found the element and it has text
    if (currentButtonText) {
    // Create the regex from the previous term, using 'g' for global match
    // Note: If previousTerm might contain special regex characters, it should be escaped first.
    // However, for typical search terms, this is often okay.
    const previousTermRegex = new RegExp(previousTerm, 'g');

    // Execute the match and store the result
    const matches = currentButtonText.match(previousTermRegex);

    // Check if matches were found (matches is not null) AND if more than one match occurred
    if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term,
        // do not replace to avoid mistakes (e.g., term 'shop' in "Search for 'shop towels'")
        return;
    }

    // If 0 or 1 match was found, proceed to replace the previous term with the new term
    // (This assumes the button text template is like "Search for '{term}'")
    const newButtonText = currentButtonText.replace(previousTerm, newTerm);
    searchForTextElement.innerText = newButtonText;
    }
}

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Suggestions: Predictive Search API
    const queryFetch = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=query,collection&section_id=predictive-search`,
      { signal: this.abortController.signal }
    );

    // Products: results-page search engine
    const productFetch = fetch(
      `${window.location.origin}/search?q=${encodeURIComponent(searchTerm)}&type=product&section_id=predictive-search-products`,
      { signal: this.abortController.signal }
    );

    Promise.all([queryFetch, productFetch])
      .then(([queryResponse, productResponse]) => {
        if (!queryResponse.ok || !productResponse.ok) {
          var error = new Error(queryResponse.status || productResponse.status);
          this.close();
          throw error;
        }

        return Promise.all([queryResponse.text(), productResponse.text()]);
      })
      .then(([queryText, productText]) => {
        const parser = new DOMParser();
        const queryDoc = parser.parseFromString(queryText, 'text/html');
        const productDoc = parser.parseFromString(productText, 'text/html');

        const querySection = queryDoc.querySelector('#shopify-section-predictive-search');
        const productSection = productDoc.querySelector('#shopify-section-predictive-search-products');

        if (!querySection) {
          this.close();
          return;
        }

        let mainResults = querySection.querySelector('#predictive-search-results');
        if (!mainResults) {
          mainResults = document.createElement('div');
          mainResults.id = 'predictive-search-results';
          mainResults.setAttribute('role', 'listbox');
          querySection.appendChild(mainResults);
        }

        const productResultsGroup = productSection?.querySelector('[data-predictive-search-products]');
        const productCards = productResultsGroup?.querySelectorAll('.grid__item') || [];
        const hasProductResults = productCards.length > 0;

        if (productResultsGroup && hasProductResults) {
          mainResults.querySelector('[data-predictive-search-no-results]')?.remove();

          let resultsWrapper = mainResults.querySelector('#predictive-search-results-groups-wrapper');

          if (resultsWrapper) {
            resultsWrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
            resultsWrapper.appendChild(productResultsGroup.cloneNode(true));
          } else {
            const wrapper = document.createElement('div');
            wrapper.id = 'predictive-search-results-groups-wrapper';
            wrapper.className =
              'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';

            while (mainResults.firstChild) {
              wrapper.appendChild(mainResults.firstChild);
            }

            wrapper.appendChild(productResultsGroup.cloneNode(true));
            mainResults.appendChild(wrapper);
          }

          const resultsCount = productResultsGroup.getAttribute('data-search-results-count');
          if (resultsCount) {
            this.allPredictiveSearchInstances.forEach((instance) => {
              instance.cachedCounts[queryKey] = parseInt(resultsCount, 10);
            });
          }
        }

        const resultsMarkup = querySection.innerHTML;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    
    // Initialize variant swatches for dynamically loaded product cards
    if (typeof initializeVariantImageEvents === 'function') {
      initializeVariantImageEvents();
    }
    
    // Fetch and update the actual search results count
    this.updateActualSearchCount();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) this.setLiveRegionText(liveRegion.textContent);
  }

  getResultsMaxHeight() {
      const headerBottom = document.querySelector('.section-header').getBoundingClientRect().bottom;
      const contentMarginTop = parseFloat(getComputedStyle(document.querySelector('.search-modal__content')).marginTop);
      
      this.resultsMaxHeight = window.innerHeight - headerBottom - contentMarginTop;
      return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  updateActualSearchCount() {
    const searchButton = this.querySelector('[data-search-results-button]');
    if (!searchButton) return;

    const resultsCountSpan = searchButton.querySelector('[data-results-count]');
    if (!resultsCountSpan) return;

    // Theme-level inventory/color guards hide results Shopify still counts, so never show a number.
    resultsCountSpan.textContent = 'SEE ALL RESULTS';
  }
}

customElements.define('predictive-search', PredictiveSearch);
